/**
 * Single source of truth for the seller platform fee story.
 *
 * Before this file existed the fee number was hardcoded in 13+ places across
 * /sell, /pricing, /faq, /refund-policy, /terms, /tax-info, EarningsSection,
 * HomeSellCTA, etc. (/seller-fees folded into /pricing.) The numbers drifted
 * between surfaces until they all imported from here.
 *
 * Every fee surface must import from here. Adding a fee number anywhere
 * else is a regression (the test suite that grepped for strays was retired with
 * the source-text tests on 2026-09-01, so this is a review rule now).
 *
 * The 2.5% launch promo ended 2026-06-30 and its API (PROMO, isPromoActive,
 * promoDaysRemaining, formatPromoEndDate) has been removed — tiered rates
 * below are the only fee schedule.
 */

import type { SubscriptionTier } from "@/lib/subscription-tiers";

/**
 * Platform fee by seller tier, as a percentage of sale.
 *
 * Mirrored in Postgres by `seller_sale_fee_percent(profile_id)`, which the
 * Stripe webhook uses when it books the fee. Two copies is one more than ideal
 * — the webhook needs it in SQL, every fee surface needs it in TS — so if you
 * change a number here, change it there.
 */
export const FEE_TIERS = {
  seller_free: 10,
  seller_pro: 7,
  seller_elite: 5,
  // Max pays no platform fee on marketplace sales at all; the $89/mo covers
  // it. Stripe's own processing fee still applies — that is Stripe's, not
  // ours, and no plan can waive it.
  seller_max: 0,
} as const;

export type SellerTier = keyof typeof FEE_TIERS;

/**
 * The fee tier a subscription tier sells at. Only the three seller plans lower
 * the fee; Free and Buyer Plus both sell at the free-tier rate — exactly what
 * `seller_sale_fee_percent()` books.
 */
export function sellerFeeTier(tier: SubscriptionTier): SellerTier {
  return tier === "seller_pro" || tier === "seller_elite" || tier === "seller_max"
    ? tier
    : "seller_free";
}

/**
 * Returns the fee percentage a tier pays.
 *
 * The type says SellerTier, but the value arrives from the database at runtime
 * — a `subscription_tier` column, an API payload, an edge function. This repo
 * has already shipped tier maps that fell out of sync with SQL (seller_max was
 * missing from three separate hardcoded lists), and an unmapped tier used to
 * return `undefined`, which makes `100 - undefined` NaN and could book a sale
 * at no fee at all.
 *
 * Unknown tiers therefore fall back to the free-tier rate. Over-charging is
 * visible and refundable; silently charging nothing is neither.
 */
export function effectiveFeePercent(tier: SellerTier): number {
  const fee = FEE_TIERS[tier];
  return typeof fee === "number" ? fee : FEE_TIERS.seller_free;
}

/** Returns the seller's revenue share for a tier (100 - fee) as a percentage. */
export function effectiveSellerSharePercent(tier: SellerTier): number {
  return 100 - effectiveFeePercent(tier);
}

/** "7%" / "10%" with one decimal stripped when whole. */
export function formatFeePercent(percent: number): string {
  return `${Number.isInteger(percent) ? percent : percent.toFixed(1)}%`;
}

/**
 * The lowest fee any plan can reach. Marketing copy that says "as little as X"
 * should read this rather than hardcoding the best tier, so adding a tier
 * never leaves a stale number on /pricing or /sell.
 */
export const LOWEST_FEE_PERCENT = Math.min(...Object.values(FEE_TIERS));

/** The most a seller keeps on any plan, as a percentage. */
export const HIGHEST_SELLER_SHARE_PERCENT = 100 - LOWEST_FEE_PERCENT;
